import { Editor } from "@tiptap/react";
import { Bold, Italic, Strikethrough, List, ListOrdered, Quote, Code, Heading2 } from "lucide-react";
import { LinkSelector } from "../../HtmlEditor/Toolbar/LinkSelector";
import { FieldSelector } from "../../HtmlEditor/Toolbar/FieldSelector";

export interface MarkdownToolbarItem {
  key: string;
  type: "button" | "component";
  component?: (editor: Editor) => React.ReactNode;
  icon?: React.ReactNode;
  action?: (editor: Editor) => void;
  isActive?: (editor: Editor) => boolean;
  isEnabled?: (editor: Editor) => boolean;
  show?: boolean;
}

export interface MarkdownToolbarConfigurations {
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  heading?: boolean;
  bulletList?: boolean;
  orderedList?: boolean;
  blockquote?: boolean;
  code?: boolean;
  link?: boolean;
  fields?: boolean;
  workflowId?: string;
}

export const createMarkdownToolbarItems = ({
  bold = true,
  italic = true,
  strike = true,
  heading = true,
  bulletList = true,
  orderedList = true,
  blockquote = true,
  code = true,
  link = true,
  fields = true,
  workflowId
}: MarkdownToolbarConfigurations): MarkdownToolbarItem[] => [
  {
    key: "bold",
    type: "button",
    icon: <Bold size={16} />,
    action: editor => editor.chain().focus().toggleBold().run(),
    isActive: editor => editor.isActive("bold"),
    isEnabled: editor => editor.can().chain().focus().toggleBold().run(),
    show: bold
  },
  {
    key: "italic",
    type: "button",
    icon: <Italic size={16} />,
    action: editor => editor.chain().focus().toggleItalic().run(),
    isActive: editor => editor.isActive("italic"),
    isEnabled: editor => editor.can().chain().focus().toggleItalic().run(),
    show: italic
  },
  {
    key: "strike",
    type: "button",
    icon: <Strikethrough size={16} />,
    action: editor => editor.chain().focus().toggleStrike().run(),
    isActive: editor => editor.isActive("strike"),
    isEnabled: editor => editor.can().chain().focus().toggleStrike().run(),
    show: strike
  },
  {
    key: "heading",
    type: "button",
    icon: <Heading2 size={16} />,
    action: editor => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: editor => editor.isActive("heading", { level: 2 }),
    isEnabled: editor => editor.can().chain().focus().toggleHeading({ level: 2 }).run(),
    show: heading
  },
  {
    key: "bulletList",
    type: "button",
    icon: <List size={16} />,
    action: editor => editor.chain().focus().toggleBulletList().run(),
    isActive: editor => editor.isActive("bulletList"),
    isEnabled: editor => editor.can().chain().focus().toggleBulletList().run(),
    show: bulletList
  },
  {
    key: "orderedList",
    type: "button",
    icon: <ListOrdered size={16} />,
    action: editor => editor.chain().focus().toggleOrderedList().run(),
    isActive: editor => editor.isActive("orderedList"),
    isEnabled: editor => editor.can().chain().focus().toggleOrderedList().run(),
    show: orderedList
  },
  {
    key: "blockquote",
    type: "button",
    icon: <Quote size={16} />,
    action: editor => editor.chain().focus().toggleBlockquote().run(),
    isActive: editor => editor.isActive("blockquote"),
    isEnabled: editor => editor.can().chain().focus().toggleBlockquote().run(),
    show: blockquote
  },
  {
    key: "code",
    type: "button",
    icon: <Code size={16} />,
    action: editor => editor.chain().focus().toggleCode().run(),
    isActive: editor => editor.isActive("code"),
    isEnabled: editor => editor.can().chain().focus().toggleCode().run(),
    show: code
  },
  {
    key: "link",
    type: "component",
    component: editor => <LinkSelector editor={editor} />,
    show: link
  },
  {
    key: "fields",
    type: "component",
    component: editor => (
      <FieldSelector
        workflowId={workflowId}
        onSelect={(path, title) => {
          //@ts-ignore
          editor?.chain().focus().addField(path, title).run();
          return true;
        }}
      />
    ),
    show: fields
  }
];
